import { setSparkflowGlobalContext } from '@thoughtspot/blink-context';
import { EXPERIMENT_KEY, SPARKFLOW_USE_CASE_BASED_ONBOARDING_VARIANT, useABTesting, } from '@thoughtspot/experimentation';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Card, CardBody } from '@thoughtspot/radiant-react/widgets/card';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useBlinkContextForSparkflow } from "../../contexts/sparkflow-blink-context";
import { getSparkflowConfig } from "../../contexts/sparkflow-context";
import { useSaveSpotbuildGeneratedIds, useUpdateFreeTrialOnboardingMutation, } from "../../services/onboarding.service";
import { agentCardClicked, agentSelectionConfirmed, PageContext, usecaseCardClicked, usecasePageErrorShown, usecaseSelectionConfirmed, usecaseSelectionPageLoaded, } from "../../utils/metrics/metrics-utils";
import { DEFAULT_FUNCTION_ID, DEFAULT_INDUSTRY_ID, getOrderedAgentOptions, getUseCaseQuestions, OTHER_FUNCTION_ID, OTHER_INDUSTRY_ID, SaveAgentIdMapping, UseCaseMode, } from "../../utils/sparkflow-constants";
import { AgentId } from "../../utils/types";
import { AppLogo } from '../spotbuild-integration/components/app-logo/app-logo.container';
import styles from './use-case.module.scss';
export const UseCaseContainer = () => {
    const { t } = useTranslation();
    const { navService, metricsService, sessionService, } = useBlinkContextForSparkflow();
    const { buildInfo, externalConfig } = getSparkflowConfig();
    const { updateSparkflowGlobalContext, requestChecklistRefresh, } = setSparkflowGlobalContext();
    const { saveSpotbuildGeneratedIds, error: saveSpotbuildError, } = useSaveSpotbuildGeneratedIds();
    const { variant, loading } = useABTesting(EXPERIMENT_KEY.SPARKFLOW_USE_CASE_BASED_ONBOARDING_VARIANT);
    const isSearchMode = variant === SPARKFLOW_USE_CASE_BASED_ONBOARDING_VARIANT.SEARCH;
    const isResearchMode = variant === SPARKFLOW_USE_CASE_BASED_ONBOARDING_VARIANT.RESEARCH;
    const isAgentSelectionMode = variant === SPARKFLOW_USE_CASE_BASED_ONBOARDING_VARIANT.AGENT_SELECTION;
    const isQuestionVariant = isSearchMode || isResearchMode;
    const isSparkflowUsecaseBasedOnboardingEnabled = sessionService.isSparkflowUsecaseBasedOnboardingEnabled();
    const orderedAgents = getOrderedAgentOptions(isSparkflowUsecaseBasedOnboardingEnabled);
    const userProperties = sessionService.getSessionInfo()?.userProperties;
    const selectedFunction = userProperties?.function === OTHER_FUNCTION_ID
        ? DEFAULT_FUNCTION_ID
        : userProperties?.function;
    const selectedIndustry = userProperties?.industry === OTHER_INDUSTRY_ID
        ? DEFAULT_INDUSTRY_ID
        : userProperties?.industry;
    const questionCards = useMemo(() => getUseCaseQuestions(selectedFunction, selectedIndustry, isResearchMode), [isResearchMode, selectedFunction, selectedIndustry]);
    const useCaseMode = isResearchMode
        ? UseCaseMode.RESEARCH
        : UseCaseMode.SEARCH;
    const preSelectedAgent = (isAgentSelectionMode && orderedAgents[1]?.id) || '';
    const items = isQuestionVariant ? questionCards : orderedAgents;
    const [selectedCard, setSelectedCard] = useState(undefined);
    const [bannerAlertMessage, setBannerAlertMessage] = useState({ isVisible: false, errorMessage: '' });
    useEffect(() => {
        if (saveSpotbuildError) {
            setBannerAlertMessage({
                isVisible: true,
                errorMessage: saveSpotbuildError.message,
            });
        }
    }, [saveSpotbuildError]);
    useEffect(() => {
        if (!loading && isAgentSelectionMode && orderedAgents[1]) {
            setSelectedCard({ ...orderedAgents[1], sequence: 2 });
        }
        if (!loading) {
            usecaseSelectionPageLoaded(metricsService, {
                pageContext: PageContext.USE_CASE_SELECTION_PAGE,
                selectedFunction: selectedFunction || '',
                selectedIndustry: selectedIndustry || '',
                experimentVariant: variant || '',
                ...(isAgentSelectionMode && { preSelectedAgent }),
            });
        }
    }, [loading]);
    const userName = sessionService?.getUserDisplayName();
    const defaultPinboardName = t('sparkflow.default.liveboard', {
        name: userName,
    });
    const useCreatePinboardByNameMutation = externalConfig?.services?.useCreatePinboardByNameMutation ??
        (() => ({ _createPinboard: undefined }));
    const { _createPinboard } = useCreatePinboardByNameMutation();
    const { Session__updateFreeTrialOnboardingConfig, } = useUpdateFreeTrialOnboardingMutation();
    const handleSelectCard = (item, sequence) => {
        setSelectedCard({ ...item, sequence });
        if (item.description && !isAgentSelectionMode) {
            updateSparkflowGlobalContext({
                preFilledQuestion: item.description,
            });
        }
        if (isQuestionVariant) {
            usecaseCardClicked(metricsService, {
                pageContext: PageContext.USE_CASE_SELECTION_PAGE,
                selectedFunction: selectedFunction || '',
                selectedIndustry: selectedIndustry || '',
                experimentVariant: variant || '',
                useCaseSelected: item.title || '',
                useCasePrompt: item.description || '',
                useCaseSequence: sequence,
                useCaseMode,
            });
        }
        else {
            agentCardClicked(metricsService, {
                pageContext: PageContext.USE_CASE_SELECTION_PAGE,
                selectedFunction: selectedFunction || '',
                selectedIndustry: selectedIndustry || '',
                preSelectedAgent,
                agentCardSelected: item.id,
            });
        }
    };
    const getDisabledTooltip = () => {
        if (!selectedCard) {
            return t(isQuestionVariant
                ? 'sparkflow.useCase.chooseQuestionToContinue'
                : 'sparkflow.useCase.chooseAgentToContinue');
        }
        return undefined;
    };
    const handleLetsGo = async () => {
        if (!selectedCard)
            return;
        const agentId = isQuestionVariant
            ? AgentId.ASK_QUESTIONS
            : selectedCard.id;
        const onboardingLocation = SaveAgentIdMapping[agentId];
        if (!onboardingLocation) {
            throw new Error(`Unrecognised agent id: ${agentId}`);
        }
        const useCaseProps = {
            pageContext: PageContext.USE_CASE_SELECTION_PAGE,
            selectedFunction: selectedFunction || '',
            selectedIndustry: selectedIndustry || '',
            experimentVariant: variant || '',
            useCaseSelected: selectedCard.title,
            useCasePrompt: selectedCard.description,
            useCaseSequence: selectedCard.sequence ?? 0,
            useCaseMode,
        };
        try {
            const promisesArr = [
                Session__updateFreeTrialOnboardingConfig(false, onboardingLocation, true),
            ];
            if (sessionService.isOnboardingMTServicesEnabled()) {
                promisesArr.push(saveSpotbuildGeneratedIds([
                    { agent_id: onboardingLocation },
                ]));
            }
            await Promise.all(promisesArr);
            sessionService.setEdgeOnboardingCompleted(true);
            sessionService.setOnboardingLocationSelected(onboardingLocation);
            if (sessionService.isSparkflowV2Enabled()) {
                requestChecklistRefresh({ checklistRefreshRequested: true });
            }
            if (isQuestionVariant) {
                navService?.goToAskSageChat([
                    { name: 'worksheetId', value: buildInfo?.modelId },
                    { name: jsonConstants.IS_FROM_SPARKFLOW, value: true },
                    { name: 'isDeepResearch', value: isResearchMode },
                ]);
                usecaseSelectionConfirmed(metricsService, useCaseProps);
                return;
            }
            if (selectedCard.id === AgentId.ASK_QUESTIONS) {
                navService?.goToAskSageChat([
                    { name: 'worksheetId', value: buildInfo?.modelId },
                    { name: jsonConstants.IS_FROM_SPARKFLOW, value: true },
                ]);
            }
            else if (selectedCard.id === AgentId.BUILD_DASHBOARD) {
                const response = await _createPinboard({
                    variables: { name: defaultPinboardName },
                });
                const newLiveboardId = response?.data?.Pinboard__createByName?.id;
                if (newLiveboardId) {
                    updateSparkflowGlobalContext({
                        isLoadingIndicatorVisible: true,
                        hideSparkflowBanner: true,
                    });
                    navService?.goToSparkflowPinboard(newLiveboardId, [
                        { name: 'mode', value: 'edit' },
                    ]);
                }
            }
            else if (selectedCard.id === AgentId.EMBED_INSIGHTS) {
                navService?.goToDevelopTab([
                    { name: jsonConstants.IS_FROM_SPARKFLOW, value: true },
                ]);
            }
            agentSelectionConfirmed(metricsService, {
                pageContext: PageContext.USE_CASE_SELECTION_PAGE,
                selectedFunction: selectedFunction || '',
                selectedIndustry: selectedIndustry || '',
                preSelectedAgent,
                agentCardSelected: selectedCard.id,
            });
        }
        catch (error) {
            setBannerAlertMessage({
                isVisible: true,
                errorMessage: error?.message,
            });
            usecasePageErrorShown(metricsService, {
                ...useCaseProps,
                errorType: error?.message || 'Navigation error',
            });
        }
    };
    const renderCard = (id, icon, title, description, isSelected, onSelect, isCenter) => (React.createElement(Card, { key: id, className: cx(styles.card, {
            [styles.cardSelected]: isSelected,
            [styles.cardCenter]: isCenter,
        }), onClick: onSelect, data: id, noBorder: true },
        React.createElement(CardBody, null,
            React.createElement(Vertical, { className: styles.cardContent },
                isSelected && (React.createElement(Horizontal, { className: styles.checkmarkBadge },
                    React.createElement(Icon, { id: IconID.CHECKMARK, size: IconSize.XSMALL, color: IconColor.WHITE }))),
                icon && (React.createElement(Icon, { id: icon, size: IconSize.XXLARGE, className: styles.iconWrapper })),
                React.createElement(Typography, { variant: "section-label", className: styles.cardTitle }, title),
                React.createElement(Typography, { variant: "body-normal", className: styles.cardDescription }, description)))));
    if (loading) {
        return (React.createElement(LoadingIndicator.Global, { className: styles.loadingIndicator },
            React.createElement(React.Fragment, null)));
    }
    return (React.createElement(React.Fragment, null,
        !isQuestionVariant && (React.createElement(GlobalAlert.Banner, { type: BannerAlertType.ERROR, "data-testid": "use-case-based-onboarding-banner-alert", message: bannerAlertMessage.errorMessage, isVisible: bannerAlertMessage.isVisible, onClose: () => setBannerAlertMessage({
                isVisible: false,
                errorMessage: '',
            }) })),
        React.createElement(Vertical, { className: styles.container, hAlignContent: "center" },
            React.createElement(AppLogo, { className: styles.logo }),
            React.createElement(Vertical, { className: styles.content, hAlignContent: "center" },
                React.createElement(Typography, { variant: "headline-large", className: styles.titleWrapper }, isQuestionVariant
                    ? t('sparkflow.useCase.selectQuestion')
                    : t('sparkflow.useCase.chooseAgent')),
                React.createElement("div", { className: styles.cardsRow }, items.map((item, index) => renderCard(item.id, item.icon, item.title, item.description, selectedCard?.id === item.id, () => handleSelectCard(item, index + 1), index === 1))),
                React.createElement(Button, { text: t('sparkflow.useCase.letsGo'), type: selectedCard ? 'primary' : 'secondary', size: "m", isDisabled: !selectedCard, tooltip: getDisabledTooltip(), onClick: handleLetsGo, className: styles.ctaButton, "data-testid": "use-case-lets-go-button", isFullWidth: true })))));
};
//# sourceMappingURL=use-case.container.js.map